import type React from "react"

/**
 * The product surface palette, in one place.
 *
 * Home declared these first and `DashboardPage/home-theme` still owns the
 * Home-only pieces, but the bell, the notification centre and the Home
 * widgets all have to look like one product — and a palette copied per
 * surface drifts. `home-theme` and the shared widget library both read this.
 *
 * Brand and status are kept apart on purpose. Blue-teal is EZBuild's own
 * colour and means "this is a control"; green/amber/red mean on track, at
 * risk or late. An amount is never green, because money is not a success.
 */
export const surfaceColor = {
  canvas: "var(--dk-page, #F6F8FA)",
  surface: "var(--dk-surface, #FFFFFF)",
  ink: "var(--dk-text, #253348)",
  inkMuted: "var(--dk-text-secondary, #697789)",
  border: "var(--dk-border, #E7EDF3)",

  /** EZBuild blue-teal. `teal` is the accessible text variant. */
  accent: "#69AEC0",
  teal: "var(--dk-primary-text, #287587)",
  tealHover: "var(--dk-primary-text, #1F5C6B)",
  tealPale: "var(--dk-selected, #EDF7F9)",

  /** Status, never used for branding. */
  onTrack: "#43A477",
  atRisk: "#D3A04F",
  delayed: "#D46D78",
  /** #D46D78 is 2.9:1 on white; this is the readable variant for text. */
  delayedInk: "var(--dk-error-text, #B23B49)",
  /** #43A477 on its own pale tint, for a status word rather than a bar. */
  onTrackInk: "var(--dk-success-text, #2F7A58)",
  atRiskInk: "var(--dk-warning-text, #8A5D13)",

  progressFill: "#43A477",
  progressTrack: "var(--dk-border, #E9EEF3)",
  warningBg: "var(--dk-warning-bg, #FDF4E7)",
  criticalBg: "var(--dk-error-bg, #FCEEEF)",
  successBg: "var(--dk-success-bg, #EDF7F1)",
  chipBg: "var(--dk-subtle, #EFF3F7)",
  chipFg: "var(--dk-text-secondary, #5A6879)",

  /**
   * Layout-editing accents. A pale wash of the brand teal, used only while a
   * surface is being customised, so "I am editing" never borrows a status
   * colour and never reads as an error.
   */
  editBg: "var(--dk-selected, #F2F9FB)",
  editBorder: "var(--dk-border-strong, #CCE4EA)",
  editOutline: "#AFD4DE"
} as const

export const surfaceText = {
  welcomeTitle: { fontSize: 29, lineHeight: "36px", fontWeight: 700 },
  welcomeSubtitle: { fontSize: 14, lineHeight: "20px", fontWeight: 400 },
  sectionTitle: { fontSize: 19, lineHeight: "25px", fontWeight: 700 },
  metricValue: { fontSize: 27, lineHeight: "33px", fontWeight: 700 },
  financeValue: { fontSize: 19, lineHeight: "26px", fontWeight: 600 },
  /** An item's own headline inside a list. */
  itemTitle: { fontSize: 15, lineHeight: "21px", fontWeight: 600 },
  actionTitle: { fontSize: 14, lineHeight: "20px", fontWeight: 600 },
  body: { fontSize: 14, lineHeight: "20px", fontWeight: 400 },
  button: { fontSize: 13, lineHeight: "18px", fontWeight: 600 },
  table: { fontSize: 13, lineHeight: "19px", fontWeight: 400 },
  /** Metadata: the record line, the timestamp, the actor. */
  meta: { fontSize: 13, lineHeight: "18px", fontWeight: 400 },
  label: { fontSize: 12, lineHeight: "16px", fontWeight: 500 },
  chip: { fontSize: 11, lineHeight: "15px", fontWeight: 600 }
} as const satisfies Record<string, React.CSSProperties>

export const surfaceSize = {
  cardRadius: 10,
  controlRadius: 6,
  /** Every toolbar control is this tall, so a row of them reads as one strip. */
  controlHeight: 36,
  cardPadding: 16,
  sectionGap: 16,
  contentGutter: 24,
  cardShadow: "var(--dk-shadow, 0 1px 2px rgba(37, 51, 72, 0.05))"
} as const

/** Money and percentages line up column to column only with tabular figures. */
export const TABULAR: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1'
}
